import { useEffect, useState } from "react";
import withRouter from "hoc/withRouter";
import {
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
} from "@mui/material";
import ListPage from "components/ListPage/ListPage";
import baggageTripsService from "services/admin/baggageTripsService";
import { useGetTrains } from "hooks/useGetTrains";
import { useGetStations } from "hooks/useGetStations";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  BAGGAGE_STATUS_LABELS,
  baggageStatusColor,
  baggageStatusLabel,
} from "pages/Admin/Baggages/Baggage/baggage-status";

// Скоуп по станционной привязке оператора (вариант B):
// any — все (для оператора это его станции); origin — приёмка; destination — выдача.
// Сотрудник без привязки к станциям видит всё во всех вкладках.
const SCOPE_TABS = [
  { value: "any", label: "Все" },
  { value: "origin", label: "Приёмка" },
  { value: "destination", label: "Выдача" },
];

const PAYMENT_OPTIONS = [
  { value: "", label: "Все" },
  { value: "paid", label: "Оплачено" },
  { value: "unpaid", label: "Не оплачено" },
  { value: "refunded", label: "Возврат" },
];

// Статус отправки (overall) — выводится на бэкенде из статусов мест.
const STATUS_OPTIONS = [
  { value: "", label: "Все" },
  ...Object.entries(BAGGAGE_STATUS_LABELS).map(([value, label]) => ({ value, label })),
];

// Пустой набор select/date-фильтров — он же используется кнопкой «Сбросить».
const EMPTY_FILTERS = {
  paymentStatus: "",
  status: "",
  dateFrom: "",
  dateTo: "",
  trainID: "",
  originStationID: "",
  destinationStationID: "",
};

const BaggageTripList = () => {
  const orgID = localStorage.getItem("orgID") || "";
  const [scope, setScope] = useState("any");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const setFilter = (partial: Partial<typeof EMPTY_FILTERS>) =>
    setFilters((prev) => ({ ...prev, ...partial }));

  // Поиск дебаунсим, чтобы не дёргать запрос на каждую клавишу.
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  const {
    data: tripsResponse,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["baggage-trips", { orgID, scope, q, ...filters }],
    queryFn: async () => {
      const params: any = {};
      if (scope !== "any") params.stationScope = scope;
      if (q) params.q = q;
      if (filters.paymentStatus) params.paymentStatus = filters.paymentStatus;
      if (filters.status) params.status = filters.status;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.trainID) params.trainID = filters.trainID;
      if (filters.originStationID) params.originStationID = filters.originStationID;
      if (filters.destinationStationID) params.destinationStationID = filters.destinationStationID;

      const response: any = await baggageTripsService.getAll(params);
      return response.data;
    },
  });

  // Справочники для select-фильтров «Поезд» и «Станции».
  const { data: trainsResponse } = useGetTrains({ orgID });
  const { data: stationsResponse } = useGetStations({ orgID });
  const trains = (trainsResponse as any)?.data || [];
  const stations = (stationsResponse as any)?.data || [];

  const rows: any[] = (tripsResponse as any)?.data || [];

  const hasActiveFilters = Boolean(
    q ||
    scope !== "any" ||
    filters.paymentStatus ||
    filters.status ||
    filters.dateFrom ||
    filters.dateTo ||
    filters.trainID ||
    filters.originStationID ||
    filters.destinationStationID,
  );

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setQInput("");
    setQ("");
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Удалить отправку?")) return;
    await baggageTripsService.delete(id);
    refetch();
  };

  function buildFilters() {
    // Скрываем панель только когда совсем нет данных и фильтры не заданы
    // (пустая выдача из-за фильтров — панель оставляем, чтобы их сбросить).
    if (rows.length === 0 && !hasActiveFilters) return null;
    return (
      <div className="flex flex-wrap gap-3 items-center m-t-16">
        <TextField
          size="small"
          label="Поиск (ФИО, билет, телефон)"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          sx={{ minWidth: 240 }}
        />

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Статус оплаты</InputLabel>
          <Select
            label="Статус оплаты"
            value={filters.paymentStatus}
            onChange={(e) => setFilter({ paymentStatus: String(e.target.value) })}
          >
            {PAYMENT_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Статус отправки</InputLabel>
          <Select
            label="Статус отправки"
            value={filters.status}
            onChange={(e) => setFilter({ status: String(e.target.value) })}
          >
            {STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          type="date"
          label="Отправление с"
          InputLabelProps={{ shrink: true }}
          value={filters.dateFrom}
          onChange={(e) => setFilter({ dateFrom: e.target.value })}
          sx={{ minWidth: 150 }}
        />
        <TextField
          size="small"
          type="date"
          label="по"
          InputLabelProps={{ shrink: true }}
          value={filters.dateTo}
          onChange={(e) => setFilter({ dateTo: e.target.value })}
          sx={{ minWidth: 150 }}
        />

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Поезд</InputLabel>
          <Select
            label="Поезд"
            value={filters.trainID}
            onChange={(e) => setFilter({ trainID: String(e.target.value) })}
          >
            <MenuItem value="">Все</MenuItem>
            {trains.map((t: any) => (
              <MenuItem key={t.id} value={t.id}>
                {t.route ? `${t.number} — ${t.route}` : t.number}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Станция отправления</InputLabel>
          <Select
            label="Станция отправления"
            value={filters.originStationID}
            onChange={(e) => setFilter({ originStationID: String(e.target.value) })}
          >
            <MenuItem value="">Все</MenuItem>
            {stations.map((s: any) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Станция назначения</InputLabel>
          <Select
            label="Станция назначения"
            value={filters.destinationStationID}
            onChange={(e) => setFilter({ destinationStationID: String(e.target.value) })}
          >
            <MenuItem value="">Все</MenuItem>
            {stations.map((s: any) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {hasActiveFilters && (
          <Button size="small" onClick={resetFilters}>
            Сбросить
          </Button>
        )}
        {isFetching && <CircularProgress size={18} />}
      </div>
    );
  }

  function buildContent() {
    if (isLoading) {
      return (
        <div className="text-center">
          <CircularProgress></CircularProgress>
        </div>
      );
    }
    if (rows.length === 0) {
      return hasActiveFilters ? (
        <div>Ничего не найдено по заданным фильтрам.</div>
      ) : (
        <div>
          Отправки отсутствуют. Чтобы добавить отправку, нажмите на кнопку "Добавить" сверху
        </div>
      );
    }
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Отправитель</TableCell>
            <TableCell>№ Поезда</TableCell>
            <TableCell>Маршрут</TableCell>
            <TableCell>Дата отправления</TableCell>
            <TableCell>№ билета</TableCell>
            <TableCell>Статус перевозки</TableCell>
            <TableCell>Статус оплаты</TableCell>
            <TableCell>Действия</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((c: any, index: number) => (
            <TableRow key={c.id}>
              <TableCell>{index + 1}</TableCell>
              <TableCell>
                {c.customer
                  ? `${c.customer.lastName || ""} ${c.customer.firstName || ""}`.trim() ||
                    c.customer.name ||
                    "—"
                  : "—"}
              </TableCell>
              <TableCell>{c.train?.number}</TableCell>
              <TableCell>
                {c.originStation?.name || "—"} → {c.destinationStation?.name || "—"}
              </TableCell>
              <TableCell>{c.departureDate}</TableCell>
              <TableCell>{c.ticketNumber}</TableCell>
              <TableCell>
                {c.shipmentStatus ? (
                  <Chip
                    size="small"
                    color={baggageStatusColor(c.shipmentStatus)}
                    label={
                      baggageStatusLabel(c.shipmentStatus) +
                      (c.pieceCount ? ` · ${c.deliveredCount}/${c.pieceCount}` : "")
                    }
                  />
                ) : (
                  "—"
                )}
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  color={c.paymentStatus === "paid" ? "success" : "default"}
                  label={
                    c.paymentStatus === "paid"
                      ? "Оплачено"
                      : c.paymentStatus === "refunded"
                        ? "Возврат"
                        : "Не оплачено"
                  }
                />
              </TableCell>
              <TableCell>
                <div className="flex gap-2 items-center">
                  <Link to={`/admin/baggages/baggage-trips/detail/${c.id}`}>
                    <Button size="small" variant="outlined">
                      Просмотр
                    </Button>
                  </Link>
                  <Link to={`/admin/baggages/list?tripID=${c.id}`}>
                    <Button size="small">Управление грузами</Button>
                  </Link>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }
  return (
    <ListPage pageTitle="Отправки" pathToAdd={`/admin/baggages/baggage-trips/add`}>
      <p>Оформление перевозки багажа </p>
      {buildFilters()}
      <div className="m-t-32">{buildContent()}</div>
    </ListPage>
  );
};

export default withRouter(BaggageTripList);
